import { useCallback, useMemo, useState } from "react";
import { useForm, type Resolver } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { Factory, Pencil, Plus, Trash2, Wrench } from "lucide-react";
import { ConfirmDialog } from "@/components/shared/confirm-dialog";
import { EmptyState } from "@/components/shared/empty-state";
import { ErrorBoundary } from "@/components/shared/error-boundary";
import { Field, FieldGrid } from "@/components/shared/form";
import {
  MasterBanner,
  MasterFilterBar,
  MasterHeader,
  MasterPage,
  MasterSearchInput,
  useMasterListShortcuts,
} from "@/components/shared/master-shell";
import { Badge, StatusDot } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input, NativeSelect } from "@/components/ui/input";
import { useStockLocations } from "@/features/locations/api";
import {
  useDeleteMachine,
  useMachines,
  useProcessTypes,
  useUpsertMachine,
} from "@/features/machines/api";
import {
  MACHINE_STATUSES,
  MACHINE_TYPES,
  labelFor,
  statusTone,
} from "@/features/machines/constants";
import { MachineMaintenanceDialog } from "@/features/machines/maintenance-dialog";
import {
  emptyMachineForm,
  machineFormSchema,
  type MachineFormValues,
} from "@/features/machines/schema";
import type { MachineListRow, MachineStatus } from "@/features/machines/types";
import { isSupabaseConfigured } from "@/lib/supabase";

export function MachinesPage() {
  return (
    <ErrorBoundary title="Machines page crashed">
      <MachinesPageInner />
    </ErrorBoundary>
  );
}

function MachinesPageInner() {
  const { data = [], isLoading, error, refetch, isFetching } = useMachines();
  const { data: processes = [] } = useProcessTypes();
  const { data: locations = [] } = useStockLocations();
  const upsert = useUpsertMachine();
  const remove = useDeleteMachine();

  const [search, setSearch] = useState("");
  const [processFilter, setProcessFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState<"live" | MachineStatus | "all">("live");
  const [editing, setEditing] = useState<MachineListRow | null>(null);
  const [open, setOpen] = useState(false);
  const [deleting, setDeleting] = useState<MachineListRow | null>(null);
  const [maintMachine, setMaintMachine] = useState<MachineListRow | null>(null);

  const form = useForm<MachineFormValues>({
    resolver: zodResolver(machineFormSchema) as unknown as Resolver<MachineFormValues>,
    defaultValues: emptyMachineForm,
  });

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return data.filter((row) => {
      if (statusFilter === "live" && row.status === "inactive") return false;
      if (statusFilter !== "live" && statusFilter !== "all" && row.status !== statusFilter) {
        return false;
      }
      if (processFilter !== "all" && row.process_type_id !== processFilter) return false;
      if (!q) return true;
      return (
        row.name.toLowerCase().includes(q) ||
        row.code.toLowerCase().includes(q) ||
        (row.process_type?.name ?? "").toLowerCase().includes(q) ||
        (row.capacity ?? "").toLowerCase().includes(q) ||
        (row.description ?? "").toLowerCase().includes(q)
      );
    });
  }, [data, search, processFilter, statusFilter]);

  const activeLocations = useMemo(
    () => locations.filter((l) => l.is_active),
    [locations],
  );

  const openCreate = useCallback(() => {
    setEditing(null);
    const nextSort = data.length ? Math.max(...data.map((d) => d.sort_order)) + 10 : 10;
    form.reset({
      ...emptyMachineForm,
      process_type_id: processes[0]?.id ?? "",
      sort_order: nextSort,
    });
    setOpen(true);
  }, [data, form, processes]);

  useMasterListShortcuts({
    searchInputId: "machines-search",
    onCreate: openCreate,
  });

  function openEdit(row: MachineListRow) {
    setEditing(row);
    form.reset({
      code: row.code,
      name: row.name,
      process_type_id: row.process_type_id,
      machine_type: row.machine_type,
      capacity: row.capacity ?? "",
      location_id: row.location_id ?? "",
      status: row.status,
      min_thickness_mm: row.min_thickness_mm ?? undefined,
      max_thickness_mm: row.max_thickness_mm ?? undefined,
      max_width_mm: row.max_width_mm ?? undefined,
      description: row.description ?? "",
      sort_order: row.sort_order,
    });
    setOpen(true);
  }

  const submit = form.handleSubmit(async (values) => {
    await upsert.mutateAsync({ id: editing?.id, values });
    setOpen(false);
  });

  const empty = !isLoading && !error && data.length === 0;
  const errors = form.formState.errors;

  return (
    <MasterPage>
      <MasterHeader
        title="Machines"
        icon={<Factory />}
        countLabel={`${data.length} machines`}
        onRefresh={() => void refetch()}
        refreshing={isFetching}
        actions={
          <Button type="button" size="sm" onClick={openCreate} disabled={!processes.length}>
            <Plus />
            New machine
          </Button>
        }
      />

      {!isSupabaseConfigured ? (
        <MasterBanner>Connect Supabase environment variables to enable live data.</MasterBanner>
      ) : null}

      {error ? <MasterBanner tone="danger">{(error as Error).message}</MasterBanner> : null}

      {empty ? (
        <EmptyState
          title="No machines yet"
          description="Seed includes HR parter, CRM, furnace, skin pass, slitter and tube mill — or add one."
          action={
            <Button type="button" size="sm" onClick={openCreate} disabled={!processes.length}>
              <Plus />
              Create machine
            </Button>
          }
        />
      ) : (
        <>
          <MasterFilterBar shownLabel={`${filtered.length} shown`}>
            <MasterSearchInput
              id="machines-search"
              value={search}
              onChange={setSearch}
              placeholder="Search code, name, process…  (/)"
            />
            <NativeSelect
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value as typeof statusFilter)}
              className="w-36"
              aria-label="Status filter"
            >
              <option value="live">Active + maint.</option>
              <option value="active">Active</option>
              <option value="under_maintenance">Under maint.</option>
              <option value="inactive">Inactive</option>
              <option value="all">All</option>
            </NativeSelect>
            <NativeSelect
              value={processFilter}
              onChange={(e) => setProcessFilter(e.target.value)}
              className="w-36"
              aria-label="Process filter"
            >
              <option value="all">All processes</option>
              {processes.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.name}
                </option>
              ))}
            </NativeSelect>
          </MasterFilterBar>

          <div className="overflow-auto rounded-md border border-border bg-card">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border text-left text-2xs uppercase tracking-[0.05em] text-muted-foreground">
                  <th className="px-2 py-1.5 font-semibold">Code</th>
                  <th className="px-2 py-1.5 font-semibold">Name</th>
                  <th className="px-2 py-1.5 font-semibold">Process</th>
                  <th className="px-2 py-1.5 font-semibold">Type</th>
                  <th className="px-2 py-1.5 font-semibold">Capacity</th>
                  <th className="px-2 py-1.5 font-semibold">Status</th>
                  <th className="px-2 py-1.5 font-semibold" />
                </tr>
              </thead>
              <tbody>
                {isLoading ? (
                  <tr>
                    <td colSpan={7} className="px-2 py-8 text-center text-xs text-muted-foreground">
                      Loading…
                    </td>
                  </tr>
                ) : filtered.length === 0 ? (
                  <tr>
                    <td colSpan={7} className="px-2 py-8 text-center text-xs text-muted-foreground">
                      No matching machines
                    </td>
                  </tr>
                ) : (
                  filtered.map((row) => (
                    <tr
                      key={row.id}
                      className="group cursor-pointer border-b border-border/70 hover:bg-muted/40"
                      onClick={() => openEdit(row)}
                    >
                      <td className="px-2 py-1 font-mono text-xs font-medium">{row.code}</td>
                      <td className="px-2 py-1">
                        <div className="font-medium leading-tight">{row.name}</div>
                        {row.location ? (
                          <div className="truncate text-2xs text-muted-foreground">
                            {row.location.code}
                          </div>
                        ) : null}
                      </td>
                      <td className="px-2 py-1">
                        <Badge variant="outline">{row.process_type?.name ?? "—"}</Badge>
                      </td>
                      <td className="px-2 py-1 text-xs text-muted-foreground">
                        {labelFor(MACHINE_TYPES, row.machine_type)}
                      </td>
                      <td className="px-2 py-1 text-xs tabular-nums">{row.capacity || "—"}</td>
                      <td className="px-2 py-1">
                        <StatusDot tone={statusTone(row.status)}>
                          {labelFor(MACHINE_STATUSES, row.status)}
                        </StatusDot>
                      </td>
                      <td className="px-2 py-1 text-right" onClick={(e) => e.stopPropagation()}>
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon-sm"
                          aria-label={`Maintenance ${row.name}`}
                          onClick={() => setMaintMachine(row)}
                        >
                          <Wrench />
                        </Button>
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon-sm"
                          aria-label={`Edit ${row.name}`}
                          onClick={() => openEdit(row)}
                        >
                          <Pencil />
                        </Button>
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon-sm"
                          aria-label={`Delete ${row.name}`}
                          className="opacity-0 transition-opacity group-hover:opacity-100 hover:text-destructive"
                          onClick={() => setDeleting(row)}
                        >
                          <Trash2 />
                        </Button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>{editing ? "Edit machine" : "New machine"}</DialogTitle>
          </DialogHeader>
          <form
            className="space-y-3"
            onSubmit={(e) => {
              e.preventDefault();
              void submit();
            }}
          >
            <FieldGrid>
              <Field label="Code" required span={4} error={errors.code?.message} htmlFor="m-code">
                <Input id="m-code" className="uppercase" {...form.register("code")} />
              </Field>
              <Field label="Name" required span={8} error={errors.name?.message} htmlFor="m-name">
                <Input id="m-name" {...form.register("name")} />
              </Field>
              <Field
                label="Process"
                required
                span={6}
                error={errors.process_type_id?.message}
                hint="Process capability"
                htmlFor="m-process"
              >
                <NativeSelect id="m-process" {...form.register("process_type_id")}>
                  <option value="" disabled>
                    Select process
                  </option>
                  {processes.map((p) => (
                    <option key={p.id} value={p.id}>
                      {p.name}
                    </option>
                  ))}
                </NativeSelect>
              </Field>
              <Field label="Machine type" span={6} htmlFor="m-type">
                <NativeSelect id="m-type" {...form.register("machine_type")}>
                  {MACHINE_TYPES.map((t) => (
                    <option key={t.value} value={t.value}>
                      {t.label}
                    </option>
                  ))}
                </NativeSelect>
              </Field>
              <Field label="Capacity" span={4} hint="e.g. 20 TPH" htmlFor="m-cap">
                <Input id="m-cap" {...form.register("capacity")} />
              </Field>
              <Field label="Status" span={4} htmlFor="m-status">
                <NativeSelect id="m-status" {...form.register("status")}>
                  {MACHINE_STATUSES.map((s) => (
                    <option key={s.value} value={s.value}>
                      {s.label}
                    </option>
                  ))}
                </NativeSelect>
              </Field>
              <Field label="Sort" span={4} htmlFor="m-sort">
                <Input id="m-sort" type="number" {...form.register("sort_order")} />
              </Field>
              <Field label="Location" span={12} hint="Optional plant place" htmlFor="m-loc">
                <NativeSelect id="m-loc" {...form.register("location_id")}>
                  <option value="">None</option>
                  {activeLocations.map((l) => (
                    <option key={l.id} value={l.id}>
                      {l.code} — {l.name}
                    </option>
                  ))}
                </NativeSelect>
              </Field>
              <Field
                label="Min thick (mm)"
                span={4}
                error={errors.min_thickness_mm?.message}
                htmlFor="m-min-t"
              >
                <Input id="m-min-t" type="number" step="0.001" {...form.register("min_thickness_mm")} />
              </Field>
              <Field label="Max thick (mm)" span={4} htmlFor="m-max-t">
                <Input id="m-max-t" type="number" step="0.001" {...form.register("max_thickness_mm")} />
              </Field>
              <Field label="Max width (mm)" span={4} htmlFor="m-max-w">
                <Input id="m-max-w" type="number" step="0.01" {...form.register("max_width_mm")} />
              </Field>
              <Field label="Description" span={12} htmlFor="m-desc">
                <Input id="m-desc" {...form.register("description")} />
              </Field>
            </FieldGrid>
            <DialogFooter>
              <Button type="button" variant="outline" size="sm" onClick={() => setOpen(false)}>
                Cancel
              </Button>
              <Button type="submit" size="sm" disabled={upsert.isPending}>
                Save
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <ConfirmDialog
        open={Boolean(deleting)}
        onOpenChange={(o) => {
          if (!o) setDeleting(null);
        }}
        title="Delete machine?"
        description={
          deleting
            ? `${deleting.name} will be removed. Prefer Inactive if production history may reference it.`
            : undefined
        }
        confirmLabel="Delete"
        destructive
        loading={remove.isPending}
        onConfirm={async () => {
          if (!deleting) return;
          await remove.mutateAsync(deleting.id);
          setDeleting(null);
        }}
      />

      <MachineMaintenanceDialog
        machine={maintMachine}
        open={Boolean(maintMachine)}
        onOpenChange={(o) => {
          if (!o) setMaintMachine(null);
        }}
      />
    </MasterPage>
  );
}
